import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { addItem } from 'Shared/actions/todos';
import { editorStateToJsonString } from 'Shared/utils/draftJsHelpers';
import { updateWorkspaceNotificationCard } from 'Shared/actions/notifications';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';

import FormFields from './FormFields/FormFields';
import ButtonsArea from './ButtonsArea/ButtonsArea';

import './CreateFeedbackAIModal.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

type Props = {
  isOpen: boolean;
  title?: string;
  teammate: {
    firstName: string;
    email: string;
  };
  toggleModal: () => void;
  workspaceNotificationCardId?: number;
};

function CreateFeedbackAIModal({
  isOpen,
  toggleModal,
  teammate,
  workspaceNotificationCardId,
  title = `Give ${teammate?.firstName || teammate?.email || 'teammate'} Feedback`,
}: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user: currentUser } = useCurrentUser();
  const privateWorkspaceId = useSelector(state => state.workspaces.find(({ isPersonal }) => isPersonal)?.id);
  const feedbackReminderNotificationId = useSelector(
    state =>
      state.workspaceNotificationCards.find(
        ({ otherUser, subtype }) =>
          otherUser?.email && otherUser?.email === teammate?.email && subtype === 'time_for_feedback'
      )?.id
  );

  const initialDueDate = moment().add(1, 'week');

  const handleKeyPress = useCallback(event => {
    if (event.which === 13 /* Enter */) event.preventDefault();
  }, []);

  const handleCreateItem = useCallback(
    async ({ dueDate, title: itemTitle }) => {
      const itemUuid = uuidv4();
      const body = editorStateToJsonString(itemTitle);
      const linkToItem = `/workspaces/${privateWorkspaceId}/item/${itemUuid}`;

      await dispatch(
        addItem({
          itemUuid,
          body,
          workspaceId: privateWorkspaceId,
          dueBy: moment(dueDate).unix(),
          isAssignCurrentUser: true,
          isFeedbackReminder: true,
        })
      );

      if (feedbackReminderNotificationId) {
        await dispatch(updateWorkspaceNotificationCard(feedbackReminderNotificationId, { deactivated_at: moment() }));
      }

      if (workspaceNotificationCardId && workspaceNotificationCardId !== feedbackReminderNotificationId) {
        await dispatch(updateWorkspaceNotificationCard(workspaceNotificationCardId, { deactivated_at: moment() }));
      }

      WpToastManager.success('Created action item', {
        actionBtnLabel: 'View',
        actionBtnFnc: () => history.push(linkToItem),
        showDismissBtn: false,
      });

      toggleModal();
    },
    [dispatch, toggleModal, privateWorkspaceId, history, feedbackReminderNotificationId]
  );

  return (
    <Form
      initialValues={{
        title,
        dueDate: initialDueDate,
        owner: currentUser,
      }}
      subscription={{
        values: false,
      }}
      onSubmit={handleCreateItem}
    >
      {({ handleSubmit }) => (
        <form onKeyPress={handleKeyPress} onSubmit={handleSubmit} autoComplete="off">
          <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
            <WpModalHeader>Create Feedback Action Item</WpModalHeader>
            <WpModalBody>
              <div className="CreateFeedbackAIModal">
                <FormFields />
              </div>
            </WpModalBody>
            <WpModalFooter>
              <ButtonsArea onToggleModal={toggleModal} onSubmit={handleSubmit} />
            </WpModalFooter>
          </WpModal>
        </form>
      )}
    </Form>
  );
}

export default CreateFeedbackAIModal;
